import cx from 'classnames';
import { ChevronDown } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { Checkbox } from '../checkbox/checkbox';
import { Input } from '../input/input';
import TruncatedTextWithTooltip from '../TruncatedTextWithTooltip/TruncatedTextWithTooltip';
import styles from './multiselect-checkbox.module.scss';

export interface MultiSelectOption {
  id: number | string;
  label: string;
}

interface MultiSelectCheckboxProps {
  options: MultiSelectOption[];
  value: (number | string)[];
  onChange: (selected: (number | string)[]) => void;
  mainOptions?: MultiSelectOption[];
  label?: string;
  allLabel?: string;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
  showSearch?: boolean;
  autoFocus?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  isUseInput?: boolean;
  disabled?: boolean;
  dropdownClassName?: string;
}

export const MultiSelectCheckbox: React.FC<MultiSelectCheckboxProps> = ({
  options,
  mainOptions,
  value,
  onChange,
  label = '',
  allLabel = 'Все',
  placeholder = 'Выбрать',
  className,
  style,
  showSearch = true,
  autoFocus = false,
  isUseInput = false,
  onOpen,
  onClose,
  disabled,
  dropdownClassName
}) => {
  const [isInputFocused, setIsInputFocused] = useState<boolean>(false);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      setSearch('');
      if (onClose) onClose();
      return;
    }
    if (onOpen) onOpen();
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  useEffect(() => {
    if (isInputFocused) {
      inputRef.current?.focus();
    }
  }, [isInputFocused]);

  const allSelected = options.length > 0 && value.length === options.length;

  const handleAllChange = () => {
    setSearch('');

    if (allSelected) {
      onChange([]);
    } else {
      onChange(options.map((o) => o.id));
    }
  };

  const handleOptionChange = (id: number | string) => {
    setSearch('');

    let newSelected: (number | string)[];
    if (value.includes(id)) {
      newSelected = value.filter((v) => v !== id);
    } else {
      newSelected = [...value, id];
    }

    onChange(newSelected);
  };

  const filteredOptions = options.filter((option) => {
    const label = (option.label || '').toLowerCase().trim();
    const searchWords = search.toLowerCase().trim().split(/\s+/);
    return searchWords.every((word) => label.includes(word));
  });

  const headerText =
    value.length === 0
      ? placeholder
      : value.length === options.length
      ? allLabel
      : (() => {
          const selectedLabels = options
            .filter((o) => value.includes(o.id))
            .map((o) => o.label);
          const joined = selectedLabels.join(', ');

          return joined;
        })();

  return (
    <div
      className={`${styles.wrapper} ${className || ''} ${
        disabled && styles.disabled
      }`}
      ref={ref}
      style={style}
      onFocus={!disabled ? () => setOpen(true) : undefined}
    >
      {label && <label className={styles.label}>{label}</label>}
      <div
        className={styles.selectHeader}
        tabIndex={0}
        style={{
          border: open ? '1px solid #429EFF' : '1px solid #e0e4ea',
        }}
      >
        <span
          className={styles.headerText}
          style={{ color: value.length === 0 ? '#8496AF' : '#35373A' }}
          onClick={!disabled ? () => setIsInputFocused(true) : undefined}
          onBlur={() => setIsInputFocused(false)}
        >
          {isUseInput && isInputFocused ? (
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Введите..."
              variant="without-borders"
              ref={inputRef}
            />
          ) : (
            headerText
          )}
        </span>
        <span
          className={
            open
              ? `${styles.downArrow} ${styles.downArrowOpen}`
              : styles.downArrow
          }
        >
          <ChevronDown />
        </span>
      </div>
      {open && (
        <div className={cx(styles.dropdown, dropdownClassName)}>
          <div
            className={styles.option}
            onClick={(e) => {
              e.preventDefault();
              handleAllChange();
            }}
          >
            <Checkbox
              checked={allSelected}
              className={styles.checkbox}
              onChange={undefined}
            />
            <TruncatedTextWithTooltip
              text={allLabel}
              maxLines={1}
              tooltipClassname={styles.optionTooltip}
              className={cx(styles.optionTooltipContent, styles.allOption)}
            />
          </div>

          <div className={styles.optionsList}>
            {mainOptions?.map((option) => (
              <div
                className={styles.option}
                key={option.id}
                onClick={(e) => {
                  e.preventDefault();
                  handleOptionChange(option.id);
                }}
                style={{ cursor: 'pointer' }}
              >
                <Checkbox
                  checked={value.includes(option.id)}
                  className={styles.checkbox}
                  onChange={undefined}
                />
                <TruncatedTextWithTooltip
                  text={option.label}
                  maxLines={1}
                  tooltipClassname={styles.optionTooltip}
                  className={styles.optionTooltipContent}
                />
              </div>
            ))}
            {filteredOptions.map((option) => {
              return (
                <div
                  className={styles.option}
                  key={option.id}
                  onClick={(e) => {
                    e.preventDefault();
                    handleOptionChange(option.id);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <Checkbox
                    checked={value.includes(option.id)}
                    className={styles.checkbox}
                    onChange={undefined}
                  />
                  <TruncatedTextWithTooltip
                    text={option.label}
                    maxLines={1}
                    tooltipClassname={styles.optionTooltip}
                    className={styles.optionTooltipContent}
                  />
                </div>
              );
            })}
            {filteredOptions.length === 0 && (
              <div
                className={styles.optionLabel}
                style={{ color: '#8496AF', padding: '8px 12px' }}
              >
                Нет совпадений
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
